import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { persistTenantSession } from "../../../helpers/api_helper";
import {
  getSaInactiveRanking,
  impersonateSaTenant,
  SaInactiveRankingRow,
} from "../../../helpers/super_admin_ops_helper";

const csvEscape = (raw: string | number | boolean): string => {
  const s = String(raw);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const downloadCsv = (
  filename: string,
  rows: Array<Array<string | number | boolean>>
): void => {
  const body = rows.map((r) => r.map(csvEscape).join(",")).join("\r\n");
  const blob = new Blob(["\uFEFF" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Ranking SA — tenants com lista fria alta (LAC-154).
 */
const SuperAdminInactiveRanking = () => {
  const { t } = useTranslation();
  document.title = t("sa.inactiveRanking.documentTitle");

  const [days, setDays] = useState(90);
  const [minPercent, setMinPercent] = useState(20);
  const [rows, setRows] = useState<SaInactiveRankingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [impersonatingId, setImpersonatingId] = useState<number | null>(null);
  const [confirmTenant, setConfirmTenant] = useState<SaInactiveRankingRow | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSaInactiveRanking({ days, min_percent: minPercent });
      if (!res?.success) {
        setError(t("sa.inactiveRanking.error.load"));
        setRows([]);
        return;
      }
      setRows(res.data || []);
    } catch (e: any) {
      setError(e?.message || t("sa.inactiveRanking.error.load"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [days, minPercent, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.info(t("sa.inactiveRanking.exportEmpty"));
      return;
    }
    setExporting(true);
    try {
      const header = [
        "tenant_id",
        "tenant_name",
        "active_count",
        "eligible_count",
        "eligible_percent",
        "days",
      ];
      const data = rows.map((row) => [
        row.tenant_id,
        row.tenant_name,
        row.active_count,
        row.eligible_count,
        row.eligible_percent,
        row.days,
      ]);
      const stamp = new Date().toISOString().slice(0, 10);
      downloadCsv(`inactive-ranking-${stamp}.csv`, [header, ...data]);
      toast.success(t("sa.inactiveRanking.exportDone"));
    } finally {
      setExporting(false);
    }
  };

  const runImpersonate = async (row: SaInactiveRankingRow) => {
    setImpersonatingId(row.tenant_id);
    try {
      const res = await impersonateSaTenant(row.tenant_id);
      if (!res?.success || !res.data?.access_token) {
        toast.error(t("sa.tenants.detail.actions.error"), { autoClose: 4000 });
        return;
      }
      const expiresIn = Number(res.data.expires_in) || 900;
      persistTenantSession({
        access_token: res.data.access_token,
        token_type: res.data.token_type,
        expires_in: expiresIn,
        user: res.data.user,
        tenant: res.data.tenant,
        impersonation: true,
        impersonation_expires_at: new Date(
          Date.now() + expiresIn * 1000
        ).toISOString(),
        impersonation_return_path: "/super-admin/inactive-ranking",
      });
      toast.success(t("sa.tenants.detail.actions.success"), { autoClose: 1500 });
      window.location.href = "/contacts/inactive";
    } catch (e: any) {
      toast.error(e?.message || t("sa.tenants.detail.actions.error"), {
        autoClose: 4000,
      });
    } finally {
      setImpersonatingId(null);
      setConfirmTenant(null);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.inactiveRanking.title")}
          pageTitle={t("sa.menu.inactiveRanking")}
        />

        <Card>
          <CardBody>
            <p className="text-muted small mb-3">
              {t("sa.inactiveRanking.intro")}
            </p>

            <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
              <div>
                <Label className="small text-muted">
                  {t("sa.inactiveRanking.daysLabel")}
                </Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                >
                  {[60, 90, 180].map((d) => (
                    <option key={d} value={d}>
                      {t("sa.inactiveRanking.daysOption", { days: d })}
                    </option>
                  ))}
                </Input>
              </div>
              <div>
                <Label className="small text-muted">
                  {t("sa.inactiveRanking.minPercentLabel")}
                </Label>
                <Input
                  type="number"
                  bsSize="sm"
                  min={0}
                  max={100}
                  style={{ width: 88 }}
                  value={minPercent}
                  onChange={(e) => setMinPercent(Number(e.target.value))}
                />
              </div>
              <Button color="primary" size="sm" disabled={loading} onClick={load}>
                {loading ? <Spinner size="sm" /> : t("sa.inactiveRanking.refresh")}
              </Button>
              <Button
                color="soft-secondary"
                size="sm"
                disabled={loading || exporting || rows.length === 0}
                onClick={exportCsv}
              >
                {exporting ? (
                  <Spinner size="sm" />
                ) : (
                  t("sa.inactiveRanking.exportCsv")
                )}
              </Button>
            </div>

            {error ? <Alert color="danger">{error}</Alert> : null}

            {loading ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <Alert color="light" className="mb-0">
                {t("sa.inactiveRanking.empty")}
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("sa.inactiveRanking.col.client")}</th>
                      <th>{t("sa.inactiveRanking.col.active")}</th>
                      <th>{t("sa.inactiveRanking.col.inactive")}</th>
                      <th>{t("sa.inactiveRanking.col.percent")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.tenant_id}>
                        <td>
                          <div>{row.tenant_name}</div>
                          <div className="text-muted small">#{row.tenant_id}</div>
                        </td>
                        <td>{row.active_count}</td>
                        <td>{row.eligible_count}</td>
                        <td>{row.eligible_percent}%</td>
                        <td className="text-end text-nowrap">
                          <Button
                            color="soft-success"
                            size="sm"
                            className="me-1"
                            disabled={impersonatingId === row.tenant_id}
                            onClick={() => setConfirmTenant(row)}
                          >
                            {impersonatingId === row.tenant_id ? (
                              <Spinner size="sm" />
                            ) : (
                              t("sa.tenants.detail.actions.impersonate")
                            )}
                          </Button>
                          <Link
                            to={`/super-admin/tenants/${row.tenant_id}`}
                            className="btn btn-sm btn-soft-primary me-1"
                          >
                            {t("sa.inactiveRanking.openTenant")}
                          </Link>
                          <Link
                            to={`/super-admin/reputation?search=${encodeURIComponent(row.tenant_name)}`}
                            className="btn btn-sm btn-soft-secondary"
                          >
                            {t("sa.inactiveRanking.openCircuit")}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={!!confirmTenant}
          toggle={() =>
            !impersonatingId && setConfirmTenant(null)
          }
        >
          <ModalHeader
            toggle={() => !impersonatingId && setConfirmTenant(null)}
          >
            {t("sa.tenants.detail.actions.impersonate")}
          </ModalHeader>
          <ModalBody>
            {t("sa.tenants.detail.actions.impersonateConfirm")}
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={!!impersonatingId}
              onClick={() => setConfirmTenant(null)}
            >
              {t("sa.inactiveRanking.cancel")}
            </Button>
            <Button
              color="primary"
              disabled={!!impersonatingId || !confirmTenant}
              onClick={() => confirmTenant && void runImpersonate(confirmTenant)}
            >
              {impersonatingId ? (
                <Spinner size="sm" />
              ) : (
                t("sa.tenants.detail.actions.impersonate")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default SuperAdminInactiveRanking;
